import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, UserCheck, AlertCircle,
  ChevronRight,
  TrendingUp, TrendingDown, Plus, Briefcase,
} from "lucide-react";
import { resolveDashboardIcon } from "../../utils/sidebarIcons";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import HolidayCard from "../holiday/HolidayCard";
import BirthdayAnniversaryCard from "./BirthdayAnniversaryCard";
import RecentJoinersCard from "./RecentJoinersCard";
import { RoleTypeEnum } from "../../utils/constants";
import {
  BirthdayAnniversaryCardSkeleton,
  HolidaysCardSkeleton,
} from "./Skeleton";

import { useAuth } from "../../store/hooks/useAuth";
import { useHasPermission } from "../../store/hooks/useRbac";
import { PERMISSIONS } from "../../utils/rbac/permissions";
import {
  useGetTodayStatusQuery,
  useGetTodayAttendanceStatsQuery,
  useGetWeeklyAttendanceOverviewQuery,
} from "../../store/apis/attendance.api";
import {
  useGetStatsQuery,
  useGetUserByIdQuery,
  useGetWorkforceAnalyticsQuery,
  useGetPendingApprovalsSummaryQuery,
} from "../../store/apis/user.api";
import { PageHeader, Button, SimpleTooltip } from "../common";

import { CHART_COLORS, WeeklyTooltip, CountTooltip, StackedBarShape } from "./dashboardUtils";

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const orgParams = useMemo(() => (user?.organizationId ? { organizationId: user.organizationId } : undefined), [user?.organizationId]);

  const { data: userStats, isLoading: loadingUserStats } = useGetStatsQuery();
  const { data: pendingApprovalsSummary, isLoading: loadingPendingApprovals } = useGetPendingApprovalsSummaryQuery(orgParams);
  const { data: todayStatusResponse } = useGetTodayStatusQuery(undefined, { skip: !user?.id });
  const { data: todayAttendanceStats, isLoading: loadingTodayStats } = useGetTodayAttendanceStatsQuery(orgParams);
  const { data: weeklyOverviewData, isLoading: loadingWeeklyOverview } = useGetWeeklyAttendanceOverviewQuery(orgParams);
  const { data: workforceAnalytics, isLoading: loadingWorkforce } = useGetWorkforceAnalyticsQuery(orgParams);


  const attendanceChartData = useMemo(() => {
    return (weeklyOverviewData?.days || []).map((d) => ({
      day: d.day,
      present: d.present,
      absent: d.absent,
    }));
  }, [weeklyOverviewData]);

  const isSuperAdmin = user?.role?.toLowerCase() === RoleTypeEnum.SUPER_ADMIN.toLowerCase();
  const { data: employeeData, isLoading: loadingEmployee } = useGetUserByIdQuery(user?.id || "", {
    skip: !user?.id || isSuperAdmin,
  });

  const loading = loadingUserStats || loadingPendingApprovals || loadingEmployee || loadingTodayStats;

  const totalEmployees = userStats?.totalEmployees || todayAttendanceStats?.totalEmployees || 0;
  const activeToday = todayAttendanceStats?.checkedIn || 0;
  const onLeave = todayAttendanceStats?.onLeave ?? 0;
  const activePct = todayAttendanceStats?.checkedInPercentage ?? (totalEmployees ? Math.round((activeToday / totalEmployees) * 100) : 0);
  const onLeavePct = todayAttendanceStats?.onLeavePercentage ?? (totalEmployees ? Math.round((onLeave / totalEmployees) * 100) : 0);

  const pendingLeavesCount = pendingApprovalsSummary?.leaves || 0;
  const arPendingCount = pendingApprovalsSummary?.regularizations || 0;
  const orgPendingTimesheets = pendingApprovalsSummary?.timesheets || 0;
  const totalPending = pendingApprovalsSummary?.total ?? (pendingLeavesCount + arPendingCount + orgPendingTimesheets);

  const deptData = useMemo(() => {
    const raw = (workforceAnalytics?.departments || []).map((d) => ({
      name: d.name,
      value: d.count,
      pct: d.pct,
    }));

    if (raw.length <= 5) return raw;

    const sorted = [...raw].sort((a, b) => b.value - a.value);
    const top4 = sorted.slice(0, 4);
    const remaining = sorted.slice(4);
    const otherValue = remaining.reduce((acc, curr) => acc + curr.value, 0);
    const otherPct = remaining.reduce((acc, curr) => acc + curr.pct, 0);

    return [
      ...top4,
      {
        name: "Other",
        value: otherValue,
        pct: Math.round(otherPct),
      },
    ];
  }, [workforceAnalytics]);

  const headcountData = useMemo(() => {
    return (workforceAnalytics?.headcountGrowth?.buckets || []).map((b) => ({
      month: b.month,
      count: b.count,
    }));
  }, [workforceAnalytics]);

  const headcountGrowthPct = workforceAnalytics?.headcountGrowth?.growthPct ?? null;

  const headcountCounts = headcountData.map((d) => d.count);
  const headcountMin = Math.min(...headcountCounts, 0);
  const headcountMax = Math.max(...headcountCounts, 1);
  const headcountDomain: [number, number] = [
    Math.max(0, Math.floor(headcountMin * 0.95)),
    Math.ceil(headcountMax * 1.05) || 1,
  ];

  useEffect(() => {
    if (!user?.id) return;
    const statusData = todayStatusResponse?.data;
    const isActive = !!statusData?.checkInTime && !statusData?.checkOutTime;
    const bridge = window.ReactNativeWebView;
    if (bridge) {
      const messageType = isActive ? "START_MOBILE_SERVICES" : "STOP_MOBILE_SERVICES";
      bridge.postMessage(
        JSON.stringify({
          type: messageType,
          payload: { status: isActive ? "checked_in" : "checked_out", source: "admin_dashboard_sync" },
        })
      );
    }
  }, [user?.id, todayStatusResponse]);

  const hours = new Date().getHours();
  const greetingTime = hours < 12 ? "Good morning" : hours < 17 ? "Good afternoon" : "Good evening";
  const greetingEmoji = hours < 12 ? "☀️" : hours < 17 ? "⛅" : "🌙";
  const greeting = `${greetingTime}, ${user?.fullName || "Admin"} ${greetingEmoji}`;

  const dateStr = new Date().toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  const canViewEmployees = useHasPermission([
    PERMISSIONS.EMPLOYEE_MANAGEMENT_VIEW,
    PERMISSIONS.EMPLOYEE_MANAGEMENT_MANAGE,
  ]);
  const canManageEmployees = useHasPermission([
    PERMISSIONS.EMPLOYEE_MANAGEMENT_MANAGE,
  ]);
  const canViewAttendanceReports = useHasPermission([
    PERMISSIONS.REPORTS_ATTENDANCE_VIEW,
    PERMISSIONS.REPORTS_ATTENDANCE_MANAGE,
    PERMISSIONS.MY_ATTENDANCE_VIEW,
  ]);
  const canApproveLeaves = useHasPermission([
    PERMISSIONS.LEAVE_REQUESTS_VIEW,
    PERMISSIONS.LEAVE_REQUESTS_MANAGE,
  ]);
  const canApproveRegularizations = useHasPermission([
    PERMISSIONS.REGULARIZATION_MANAGEMENT_VIEW,
    PERMISSIONS.REGULARIZATION_MANAGEMENT_MANAGE,
  ]);
  const canApproveTimesheets = useHasPermission([
    PERMISSIONS.TIMESHEET_MANAGEMENT_VIEW,
    PERMISSIONS.TIMESHEET_MANAGEMENT_MANAGE,
  ]);

  const statCards = useMemo(() => [
    {
      icon: resolveDashboardIcon("total-employees"),
      value: totalEmployees,
      label: "Total Employees",
      sub: `${workforceAnalytics?.newThisMonth ?? userStats?.newEmployeesThisMonth ?? 0} added this month`,
      iconBg: "var(--color-primary-100)",
      iconColor: "var(--color-primary-600)",
      trend:
        headcountGrowthPct !== null
          ? `${headcountGrowthPct >= 0 ? "+" : ""}${headcountGrowthPct}%`
          : userStats?.newEmployeesThisMonth
            ? `+${userStats.newEmployeesThisMonth}`
            : "+0%",
      trendUp: (headcountGrowthPct ?? 0) >= 0,
      trendIcon: TrendingUp,
      path: canViewEmployees ? "/employees" : undefined,
    },
    {
      icon: resolveDashboardIcon("active-today"),
      value: activeToday,
      label: "Active Today",
      sub: `${activePct}% of total checked in`,
      iconBg: "var(--color-success-light)",
      iconColor: "var(--color-success)",
      trend: `${activePct}% active`,
      trendUp: true,
      trendIcon: TrendingUp,
      path: canViewAttendanceReports ? "/attendance/today" : undefined,
    },
    {
      icon: resolveDashboardIcon("on-leave"),
      value: onLeave,
      label: "On Leave",
      sub: `${onLeavePct}% of total on leave`,
      iconBg: "var(--color-warning-light)",
      iconColor: "var(--color-warning-dark)",
      trend: `${onLeavePct}% on leave`,
      trendUp: false,
      path: canViewAttendanceReports ? "/attendance/today" : undefined,
    },
    {
      icon: resolveDashboardIcon("pending-approvals"),
      value: totalPending,
      label: "Pending Approvals",
      sub: `${pendingLeavesCount} leave · ${arPendingCount} AR · ${orgPendingTimesheets} TS`,
      iconBg: "var(--color-error-light)",
      iconColor: "var(--color-error-dark)",
      trend: totalPending > 0 ? `+${totalPending} pending` : "All clear",
      trendUp: totalPending === 0,
      trendDown: totalPending > 0,
      trendIcon: totalPending > 0 ? AlertCircle : TrendingUp,
      path: (pendingLeavesCount > 0 && canApproveLeaves)
        ? "/leave-approvals"
        : (arPendingCount > 0 && canApproveRegularizations)
          ? "/admin/regularization"
          : (canApproveTimesheets ? "/timesheet/management" : undefined),
    },
  ], [totalEmployees, activeToday, onLeave, activePct, onLeavePct, totalPending, pendingLeavesCount, arPendingCount, orgPendingTimesheets, userStats, workforceAnalytics, headcountGrowthPct, canViewEmployees, canViewAttendanceReports, canApproveLeaves, canApproveRegularizations, canApproveTimesheets]);

  const pendingItems = useMemo(() => [
    { icon: resolveDashboardIcon("approval-leave"), label: "Leave Requests", sub: "awaiting review", count: pendingLeavesCount, bg: "var(--color-warning-light)", color: "var(--color-warning-dark)", path: canApproveLeaves ? "/leave-approvals" : undefined },
    { icon: resolveDashboardIcon("approval-regularization"), label: "Regularizations", sub: "attendance fixes", count: arPendingCount, bg: "var(--color-success-light)", color: "var(--color-success)", path: canApproveRegularizations ? "/admin/regularization" : undefined },
    { icon: resolveDashboardIcon("approval-timesheet"), label: "Timesheets", sub: "week pending", count: orgPendingTimesheets, bg: "var(--color-info-light)", color: "var(--color-info)", path: canApproveTimesheets ? "/timesheet/management" : undefined },
  ], [pendingLeavesCount, arPendingCount, orgPendingTimesheets, canApproveLeaves, canApproveRegularizations, canApproveTimesheets]);

  return (
    <div className="w-full">
      <PageHeader
        title={greeting}
        description={`${dateStr} · Here's what's happening across your organization today.`}
        breadcrumbs={[{ label: "Home", path: "/dashboard" }, { label: "Dashboard" }]}
        actions={
          canManageEmployees ? (
            <Button
              appearance="primary"
              size="middle"
              icon={<Plus className="w-[17px] h-[17px]" />}
              onClick={() => navigate("/employees")}
            >
              Add Employee
            </Button>
          ) : undefined
        }
      />

      {/* STAT CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-5 sm:mb-6">
        {statCards.map((card) => {
          const Icon = card.icon;
          const TrendIcon = card.trendIcon;
          return (
            <div
              key={card.label}
              role="button"
              tabIndex={0}
              onClick={() => card.path && navigate(card.path)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  card.path && navigate(card.path);
                }
              }}
              className="bg-surface border border-border-secondary rounded-card p-5 shadow-soft transition-all duration-200 hover:-translate-y-1 hover:shadow-md cursor-pointer flex flex-col justify-between focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
            >
              {/* Row 1: Top Icon (left) + Trend Badge (right) */}
              <div className="flex items-center justify-between">
                <div
                  className="w-[42px] h-[42px] rounded-md flex items-center justify-center flex-none"
                  style={{ background: card.iconBg }}
                >
                  <Icon className="w-5 h-5" style={{ color: card.iconColor }} />
                </div>
                {card.trend && (
                  <span
                    className={`inline-flex items-center gap-1 text-[12px] font-bold px-2.5 py-0.5 rounded-full ${card.trendUp
                      ? "bg-success-light text-success-dark"
                      : card.trendDown
                        ? "bg-error-light text-error-dark"
                        : "bg-warning-light text-warning-dark"
                      }`}
                  >
                    {TrendIcon && <TrendIcon className="w-3.5 h-3.5" />}
                    {card.trend}
                  </span>
                )}
              </div>

              {/* Row 2: Metric Value */}
              <div className="mt-4 text-stat-value font-extrabold text-text-primary leading-none tracking-tight">
                {card.value.toLocaleString()}
              </div>

              {/* Row 3: Primary Label */}
              <div className="mt-1.5 text-label font-semibold text-text-secondary">
                {card.label}
              </div>

              {/* Row 4: Supporting Subtitle */}
              <div className="mt-1 text-body-xs font-normal text-text-tertiary truncate" title={card.sub}>
                {card.sub}
              </div>
            </div>
          );
        })}
      </div>

      {/* ROW 1: ATTENDANCE OVERVIEW (LEFT) + PENDING APPROVALS (RIGHT) — EQUAL HEIGHT */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.62fr_1fr] gap-4 sm:gap-5 items-stretch mb-4 sm:mb-5">
        {/* ATTENDANCE OVERVIEW */}
        <div className="bg-surface border border-border-secondary rounded-card p-5 shadow-soft flex flex-col justify-between min-w-0 h-full">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3 shrink-0">
            <div>
              <h2 className="text-heading-sm font-bold text-text-primary tracking-tight">Attendance Overview</h2>
              <p className="text-[12.5px] text-text-tertiary font-normal mt-0.5">Daily present rate · this week</p>
            </div>
            <div className="flex items-center gap-3 sm:gap-4 text-body-sm font-medium text-text-secondary">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-[3px]" style={{ background: "var(--color-chart-1)" }} />
                Present
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-[3px]" style={{ background: "var(--color-chart-5)" }} />
                Absent
              </span>
            </div>
          </div>
          <div className="h-[185px] min-h-[185px] w-full min-w-0">
            {loadingWeeklyOverview ? (
              <div className="w-full h-full flex items-center justify-center">
                <div className="animate-pulse flex items-end gap-3 h-[140px] w-full px-4">
                  {[40, 70, 55, 85, 60, 30, 20].map((h, i) => (
                    <div key={i} className="flex-1 bg-border-secondary/60 rounded-t" style={{ height: `${h}%` }} />
                  ))}
                </div>
              </div>
            ) : attendanceChartData.length === 0 ? (
              <div className="w-full h-full flex flex-col items-center justify-center text-text-tertiary text-body-sm">
                <span>No attendance data for this week</span>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={185} minHeight={185}>
                <BarChart data={attendanceChartData} barCategoryGap={6}>
                  <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "var(--color-text-tertiary)", fontWeight: 600 }} />
                  <YAxis hide />
                  <Tooltip content={<WeeklyTooltip />} cursor={{ fill: "transparent" }} />
                  <Bar
                    dataKey="present"
                    stackId="a"
                    fill="var(--color-chart-1)"
                    maxBarSize={30}
                    shape={(props: any) => {
                      const isTop = !props.payload?.absent || props.payload.absent <= 0;
                      return <StackedBarShape {...props} isTop={isTop} isBottom radius={4} />;
                    }}
                  />
                  <Bar
                    dataKey="absent"
                    stackId="a"
                    fill="var(--color-chart-5)"
                    maxBarSize={30}
                    shape={(props: any) => {
                      return <StackedBarShape {...props} isTop={true} isBottom={!(props.payload?.present > 0)} radius={4} />;
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* PENDING APPROVALS */}
        <div className="bg-surface border border-border-secondary rounded-card p-5 shadow-soft flex flex-col justify-between min-w-0 h-full">
          <div className="flex items-center justify-between mb-3 shrink-0">
            <h2 className="text-heading-sm font-bold text-text-primary tracking-tight">Pending Approvals</h2>
            <span className="text-caption font-bold px-2.5 py-1 rounded-full bg-primary-50 text-primary-700">
              {totalPending} total
            </span>
          </div>
          <div className="flex flex-col justify-between gap-2.5 flex-1">
            {pendingItems.map((item) => (
              <div
                key={item.label}
                role="button"
                tabIndex={0}
                onClick={() => item.path && navigate(item.path)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    item.path && navigate(item.path);
                  }
                }}
                className="flex items-center gap-3 p-3 rounded-xl transition-all duration-150 bg-layout-hover hover:bg-primary-50 cursor-pointer focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none min-h-[44px]"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-none" style={{ background: item.bg }}>
                  <item.icon className="w-5 h-5" style={{ color: item.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-body-md font-bold text-text-primary">{item.label}</div>
                  <div className="text-[11.5px] text-text-tertiary font-medium">{item.sub}</div>
                </div>
                <div className="flex items-center gap-2 flex-none">
                  <span className="text-[17px] font-extrabold text-text-primary">{item.count}</span>
                  <ChevronRight className="w-4 h-4 text-text-tertiary" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 2-COLUMN SECTION: PRIMARY METRICS & LISTS (LEFT) + BREAKDOWNS & FEEDS (RIGHT) */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.62fr_1fr] gap-4 sm:gap-5 items-start">
        {/* LEFT COLUMN: RECENT JOINERS + HEADCOUNT GROWTH */}
        <div className="flex flex-col gap-4 sm:gap-5 min-w-0">
          <RecentJoinersCard mode="tabs" />

          {/* HEADCOUNT GROWTH */}
          <div className="bg-surface border border-border-secondary rounded-card p-5 shadow-soft min-w-0">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-heading-sm font-bold text-text-primary tracking-tight">Headcount Growth</h2>
                <p className="text-[12.5px] text-text-tertiary font-normal mt-0.5">Total employees · last 6 months</p>
              </div>
              {headcountGrowthPct !== null && (
                <span
                  className={`inline-flex items-center gap-1 text-caption font-bold px-2.5 py-1 rounded-full ${headcountGrowthPct >= 0
                    ? "bg-success-light text-success-dark"
                    : "bg-error-light text-error-dark"
                    }`}
                >
                  {headcountGrowthPct >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                  {headcountGrowthPct >= 0 ? "+" : ""}
                  {headcountGrowthPct}%
                </span>
              )}
            </div>
            <div className="h-[160px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={headcountData} barCategoryGap={8}>
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "var(--color-text-tertiary)", fontWeight: 600 }} />
                  <YAxis hide domain={headcountDomain} />
                  <Tooltip content={<CountTooltip />} cursor={{ fill: "transparent" }} />
                  <Bar
                    dataKey="count"
                    fill="url(#headcountGradient)"
                    radius={[6, 6, 0, 0]}
                    maxBarSize={38}
                  />
                  <defs>
                    <linearGradient id="headcountGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--color-primary-500)" />
                      <stop offset="100%" stopColor="var(--color-primary-300)" />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: WORKFORCE BY DEPT + CELEBRATIONS + UPCOMING HOLIDAYS */}
        <div className="flex flex-col gap-4 sm:gap-5 min-w-0">
          {/* WORKFORCE BY DEPARTMENT */}
          <div className="bg-surface border border-border-secondary rounded-card p-5 shadow-soft min-w-0">
            <h2 className="text-heading-sm font-bold text-text-primary mb-4 tracking-tight">Workforce by Department</h2>
            {loadingWorkforce ? (
              <div className="flex items-center gap-4 py-2 min-h-[170px]">
                <div className="w-[120px] h-[120px] rounded-full bg-[var(--color-bg-skeleton)] animate-pulse flex-none" />
                <div className="flex-1 flex flex-col gap-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-[var(--color-bg-skeleton)] animate-pulse" />
                      <span className="h-3 bg-[var(--color-bg-skeleton)] animate-pulse rounded flex-1" />
                      <span className="h-3 w-8 bg-[var(--color-bg-skeleton)] animate-pulse rounded" />
                    </div>
                  ))}
                </div>
              </div>
            ) : deptData.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-center py-6 px-4 min-h-[170px]">
                <div className="w-10 h-10 rounded-full bg-layout-hover flex items-center justify-center mb-2.5 text-text-tertiary">
                  <Briefcase className="w-5 h-5" />
                </div>
                <p className="text-body-sm font-semibold text-text-secondary">No department data available</p>
                <p className="text-caption text-text-tertiary mt-0.5">Departments will appear once assigned to employees</p>
              </div>
            ) : (
              <div className="flex items-center gap-5 py-1">
                <div className="relative w-[128px] h-[128px] flex-none flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={deptData}
                        dataKey="pct"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={36}
                        outerRadius={58}
                        strokeWidth={0}
                      >
                        {deptData.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-center">
                    <span className="text-[18px] font-extrabold text-text-primary leading-none">
                      {totalEmployees}
                    </span>
                    <span className="text-[9px] font-bold text-text-tertiary uppercase tracking-wider mt-0.5">
                      EMPLOYEES
                    </span>
                  </div>
                </div>
                <div className="flex-1 flex flex-col gap-2 min-w-0">
                  {deptData.map((d, i) => (
                    <div key={d.name} className="flex items-center gap-2 min-w-0">
                      <span
                        className="w-2.5 h-2.5 rounded-[3px] flex-none"
                        style={{ background: CHART_COLORS[i % CHART_COLORS.length] }}
                      />
                      <SimpleTooltip label={d.name} side="top" className="flex-1 min-w-0">
                        <span className="text-body-sm text-text-secondary font-medium block truncate cursor-default">{d.name}</span>
                      </SimpleTooltip>
                      <span className="text-body-sm font-bold text-text-primary flex-none">{d.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* CELEBRATIONS TODAY */}
          <div>
            {loading ? <BirthdayAnniversaryCardSkeleton /> : <BirthdayAnniversaryCard variant="list" />}
          </div>

          {/* UPCOMING HOLIDAYS */}
          <div>
            {loading ? <HolidaysCardSkeleton /> : <HolidayCard limit={3} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
